import React from "react";
import { PanelHeader } from "./ScenarioPanel";
import { COMPARE_MODES } from "../data/models";
import { Lightning, CurrencyDollar, Gauge, ShieldCheck } from "@phosphor-icons/react";

export const BloomPanel = ({ bloom, compareMode, setCompareMode }) => {
  if (!bloom) return null;
  const { ranked, selected, reason } = bloom;

  return (
    <section
      data-testid="bloom-panel"
      className="panel-in w-[340px] shrink-0 h-full flex flex-col bg-white border border-stone-200 rounded-sm"
    >
      <PanelHeader layer="bloom" title="AI routing layer" subtitle="Cost · Quality · Latency · Risk" />

      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4 scroll-soft">
        {/* Compare mode */}
        <div>
          <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1.5">
            Compare mode
          </div>
          <div data-testid="compare-mode" className="flex items-center gap-1 bg-stone-100 rounded-sm p-0.5">
            {COMPARE_MODES.map((m) => (
              <button
                key={m.id}
                data-testid={`compare-${m.id}`}
                onClick={() => setCompareMode(m.id)}
                className={`flex-1 text-[11px] font-medium px-2 py-1 rounded-sm transition-colors ${
                  compareMode === m.id ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-700"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {/* Selected route */}
        <div className="border border-blue-200 bg-blue-50/50 rounded-sm p-3" data-testid="bloom-selected">
          <div className="flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-wider text-blue-700 font-medium">
              Selected route
            </span>
            <span className="font-mono text-[10px] text-blue-700/70">
              score {selected.score}
            </span>
          </div>
          <div className="text-base font-medium text-blue-950 mt-1">
            {selected.model.label}
          </div>
          <div className="text-[11px] text-blue-700/80 font-mono mt-0.5">
            {selected.model.family}
          </div>

          <div className="grid grid-cols-2 gap-2 mt-3">
            <Stat icon={<CurrencyDollar size={11} />} label="Cost / 1k tok" value={`$${selected.model.cost_per_1k.toFixed(4)}`} />
            <Stat icon={<Lightning size={11} />} label="Latency" value={`${selected.model.latency_ms}ms`} />
            <Stat icon={<Gauge size={11} />} label="Quality" value={`${selected.model.quality}/100`} />
            <Stat icon={<ShieldCheck size={11} />} label="Risk handling" value={`${selected.model.risk_handling}/100`} />
          </div>

          <div className="mt-3 pt-2.5 border-t border-blue-200/70">
            <div className="text-[11px] uppercase tracking-wider text-blue-700/80 mb-1">
              Why this route
            </div>
            <p className="text-xs text-blue-950/90 leading-relaxed">{reason}</p>
          </div>
        </div>

        {/* Candidates */}
        <div>
          <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1.5">
            Candidate model paths
          </div>
          <div className="flex flex-col gap-1.5">
            {ranked.map((r) => {
              const isSelected = r.model.id === selected.model.id;
              return (
                <div
                  key={r.model.id}
                  data-testid={`bloom-candidate-${r.model.id}`}
                  className={`p-2.5 rounded-sm border ${
                    isSelected ? "border-blue-400 bg-blue-50/50" : "border-stone-200 bg-white"
                  }`}
                >                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-stone-900">{r.model.label}</div>
                    <div className="font-mono text-[11px] text-stone-700">{r.score}</div>
                  </div>
                  <div className="text-[10px] text-stone-500 font-mono mt-0.5">
                    ${r.model.cost_per_1k.toFixed(4)}/1k · {r.model.latency_ms}ms · q{r.model.quality} · r{r.model.risk_handling}
                  </div>
                  <div className="grid grid-cols-4 gap-1 mt-1.5">
                    <Bar label="cost" value={r.breakdown.cost} />
                    <Bar label="lat" value={r.breakdown.latency} />
                    <Bar label="qual" value={r.breakdown.quality} />
                    <Bar label="risk" value={r.breakdown.risk} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

const Stat = ({ icon, label, value }) => (
  <div className="bg-white border border-blue-200/70 rounded-sm p-1.5">
    <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-blue-700/70">
      {icon}
      {label}
    </div>
    <div className="font-mono text-xs text-blue-950 mt-0.5">{value}</div>
  </div>
);

const Bar = ({ label, value }) => (
  <div>
    <div className="h-1 bg-stone-100 rounded-full overflow-hidden">
      <div className="h-full bg-stone-700 rounded-full" style={{ width: `${value}%` }} />
    </div>
    <div className="text-[9px] uppercase tracking-wider text-stone-400 mt-0.5 font-mono">
      {label}
    </div>
  </div>
);
